// The studio's background layer: a gradient, a picture or a built-in backdrop, fixed behind the
// page (the same trick glass uses: iOS ignores background-attachment: fixed, so it is a layer).
// Everything it draws comes from a typed StudioBackground (lib/themeStudio.ts), never from a
// string a person typed; the picture is our own blob URL.
import { api } from '@/runtime'
import { Backdrop } from '@/os/Backdrop'
import type { PhoneWallpaper } from '@/os/layout'
import { useBlobUrls } from '@/hooks/useBlobUrls'
import { createBlobUrls } from '@/lib/blobUrls'
import type { StudioBackground as Draw } from '@/lib/themeStudio'

const urls = createBlobUrls(8)
const load = (id: string) => api.blob(id)

const Picture = ({
  blob,
  fit,
  dim,
  blur,
}: {
  blob: string
  fit: 'cover' | 'contain'
  dim: number
  blur: number
}) => {
  const ids = [blob]
  const { urls: got } = useBlobUrls(urls, ids, load)
  const src = got[blob]
  return (
    <>
      {src && (
        <div
          className="absolute inset-0 bg-center bg-no-repeat"
          style={{
            backgroundImage: `url("${src}")`,
            backgroundSize: fit,
            filter: blur ? `blur(${blur}px)` : undefined,
            transform: blur ? 'scale(1.06)' : undefined,
          }}
        />
      )}
      {dim > 0 && <div className="absolute inset-0 bg-ink" style={{ opacity: dim / 100 }} />}
    </>
  )
}

/** `contained` draws inside its parent (the editor's preview) instead of behind the whole page. */
export const StudioBackground = ({ draw, contained }: { draw: Draw; contained?: boolean }) => {
  if (draw.kind === 'none') return null
  return (
    <div
      className={
        contained
          ? 'absolute inset-0 pointer-events-none overflow-hidden'
          : 'fixed inset-0 -z-10 pointer-events-none overflow-hidden'
      }
      aria-hidden
      data-testid="studio-background"
    >
      {draw.kind === 'gradient' && (
        <div className="absolute inset-0" style={{ background: 'var(--studio-bg)' }} />
      )}
      {draw.kind === 'image' && <Picture blob={draw.blob} fit={draw.fit} dim={draw.dim} blur={draw.blur} />}
      {draw.kind === 'backdrop' && <Backdrop kind={draw.id as PhoneWallpaper} />}
    </div>
  )
}
